'use client';
import { faCheck, faChevronDown } from '@fortawesome/free-solid-svg-icons'; // 1. Import Chevron
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Expandable, ExpandableContent, ExpandableTrigger } from '@ui/AnimatedComponents/Expandable';
import { easeInOut, motion } from 'framer-motion';
import type { FunctionItem } from '~/data/modules/types';
import { cn } from '~/lib/cn';

interface Props {
    className?: string;
    items: FunctionItem[];
}

const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: (index: number) => ({
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.1,
            ease: easeInOut,
            delay: index * 0.05
        }
    })
};

export const SidebarFeatureListSection: React.FC<Props> = ({ items, className }) => {
    return (
        <aside
            className={cn(
                'tw-[overflow-anchor:none] tw-relative tw-space-y-3 tw-border tw-py-2 tw-pl-2 tw-pr-0',
                className
            )}
        >
            {items.map((item, index) => (
                <motion.div
                    key={index}
                    className={cn('tw-rounded-xl tw-p-0 tw-text-sm tw-shadow-s')}
                    variants={itemVariants}
                    initial="hidden"
                    whileInView="visible"
                    style={{ willChange: 'opacity, transform' }}
                    viewport={{ amount: 0.1, once: true, margin: '0px 0px -50px 0px' }}
                    custom={index}
                >
                    {item.descriptionLong ? (
                        <Expandable className="tw-flex tw-flex-col">
                            {({ isExpanded }) => (
                                <>
                                    <ExpandableTrigger
                                        className="tw-mt-1 tw-cursor-pointer tw-p-2"
                                        aria-expanded={isExpanded}
                                    >
                                        <div className="tw-flex tw-items-start tw-gap-3">
                                            <div className="tw-flex-1">
                                                <p className="tw-m-0 tw-flex tw-justify-between tw-text-sm tw-font-semibold tw-text-accents-9">
                                                    {item.name}
                                                </p>
                                                {item.descriptionShort && (
                                                    <p className="tw-m-0 tw-mt-1 tw-text-xs tw-text-accents-7">
                                                        {item.descriptionShort}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="tw-self-end">
                                                <motion.div
                                                    initial={false}
                                                    animate={{ rotate: isExpanded ? 180 : 0 }}
                                                    transition={{ duration: 0.15 }}
                                                    className="tw-mt-auto tw-text-accents-6"
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faChevronDown}
                                                        size="xs"
                                                        style={{ fontSize: '10px' }}
                                                    />
                                                </motion.div>
                                            </div>
                                        </div>
                                    </ExpandableTrigger>
                                    <ExpandableContent keepMounted className="tw-px-3 tw-text-xs tw-text-accents-7">
                                        <p className="tw-m-0 tw-pb-2 tw-leading-relaxed">{item.descriptionLong}</p>
                                    </ExpandableContent>
                                </>
                            )}
                        </Expandable>
                    ) : (
                        <div className="tw-flex tw-items-start tw-gap-3 tw-p-2">
                            <div className="tw-flex-1">
                                <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-accents-9">{item.name}</p>
                                {item.descriptionShort && (
                                    <p className="tw-m-0 tw-mt-1 tw-text-xs tw-text-accents-7">
                                        {item.descriptionShort}
                                    </p>
                                )}
                            </div>
                            <div className="tw-flex tw-flex-col tw-items-end tw-justify-between">
                                <div className="tw-flex tw-h-[20px] tw-w-[20px] tw-items-center tw-justify-center tw-rounded-full tw-bg-successLight tw-text-success">
                                    <FontAwesomeIcon icon={faCheck} size="2xs" />
                                </div>
                            </div>
                        </div>
                    )}
                </motion.div>
            ))}
        </aside>
    );
};
